interface FingerprintData {
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  hardwareConcurrency: number;
  colorDepth: number;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  pixelRatio: number;
  touchSupport: boolean;
  onlineStatus: boolean;
  memoryInfo?: any;
  canvasFingerprint?: string;
}

export class FingerprintGenerator {
  private static readonly STORAGE_KEY = 'user_fingerprint_id';
  private static readonly FALLBACK_STORAGE_KEY = 'user_fallback_id';
  
  /**
   * Generate a comprehensive browser fingerprint
   */
  static async generateFingerprint(): Promise<string> {
    try {
      const fingerprintData = await this.collectFingerprintData();
      const fingerprintString = this.serializeFingerprintData(fingerprintData);
      return this.hashFingerprint(fingerprintString);
    } catch (error) {
      console.warn('Fingerprint generation failed:', error);
      return this.generateFallbackId();
    }
  }

  /**
   * Collect all available browser attributes for fingerprinting
   */
  private static async collectFingerprintData(): Promise<FingerprintData> {
    const screenObj = window.screen;
    const data: FingerprintData = {
      userAgent: navigator.userAgent,
      screenResolution: `${screenObj.width}x${screenObj.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      colorDepth: screenObj.colorDepth,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      pixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window,
      onlineStatus: navigator.onLine,
    };

    // Add memory info if available (Chrome)
    if ('memory' in performance) {
      data.memoryInfo = (performance as any).memory;
    }

    // Add canvas fingerprint
    try {
      data.canvasFingerprint = await this.generateCanvasFingerprint();
    } catch (error) {
      console.warn('Canvas fingerprint generation failed:', error);
    }

    return data;
  }

  /**
   * Generate a canvas-based fingerprint
   */
  private static generateCanvasFingerprint(): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        canvas.width = 200;
        canvas.height = 50;
        
        // Draw text with different fonts and styles
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Canvas fingerprint 🔒', 2, 2);
        
        ctx.font = '18px serif';
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Security test', 4, 20);
        
        // Add some geometric shapes
        ctx.beginPath();
        ctx.arc(50, 25, 20, 0, Math.PI * 2);
        ctx.stroke();
        
        // Convert to data URL
        const dataURL = canvas.toDataURL();
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Serialize fingerprint data to a string
   */
  private static serializeFingerprintData(data: FingerprintData): string {
    const orderedKeys = Object.keys(data).sort();
    return orderedKeys.map(key => `${key}:${JSON.stringify(data[key as keyof FingerprintData])}`).join('|');
  }

  /**
   * Hash the fingerprint string using Web Crypto API
   */
  private static async hashFingerprint(fingerprintString: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(fingerprintString);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.warn('Crypto API hashing failed, using fallback:', error);
      return this.fallbackHash(fingerprintString);
    }
  }

  /**
   * Fallback hash function for environments without Web Crypto API
   */
  private static fallbackHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Generate a fallback ID using crypto.randomUUID or other methods
   */
  private static generateFallbackId(): string {
    // Try crypto.randomUUID first (modern browsers)
    if ('randomUUID' in crypto) {
      try {
        return crypto.randomUUID();
      } catch (error) {
        console.warn('crypto.randomUUID failed:', error);
      }
    }

    // Fallback to manual UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Get or create a persistent user ID using fingerprinting + localStorage
   */
  static async getOrCreateUserId(): Promise<string> {
    try {
      // First try to get from localStorage
      const storedId = localStorage.getItem(this.STORAGE_KEY);
      if (storedId) {
        return storedId;
      }

      // Generate new fingerprint-based ID
      const fingerprintId = await this.generateFingerprint();
      
      // Store in localStorage for persistence
      localStorage.setItem(this.STORAGE_KEY, fingerprintId);
      return fingerprintId;
      
    } catch (error) {
      console.warn('Primary user ID generation failed, using fallback:', error);
      return this.getFallbackUserId();
    }
  }

  /**
   * Get fallback user ID from localStorage or generate new one
   */
  private static getFallbackUserId(): string {
    try {
      const fallbackId = localStorage.getItem(this.FALLBACK_STORAGE_KEY);
      if (fallbackId) {
        return fallbackId;
      }

      const newId = this.generateFallbackId();
      localStorage.setItem(this.FALLBACK_STORAGE_KEY, newId);
      return newId;
    } catch (error) {
      console.error('All user ID generation methods failed:', error);
      // Last resort: use timestamp + random
      return `fallback_${Date.now()}_${Math.random().toString(36).substring(2)}`;
    }
  }

  /**
   * Clear stored fingerprint data (for testing or privacy compliance)
   */
  static clearStoredFingerprint(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.FALLBACK_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear stored fingerprint:', error);
    }
  }

  /**
   * Check if fingerprinting is supported in current environment
   */
  static isFingerprintingSupported(): boolean {
    return typeof window !== 'undefined' && 
           typeof navigator !== 'undefined' && 
           typeof window.screen !== 'undefined';
  }
} 